import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { jobApplicationService } from '../services/jobApplicationService';

const JobApplicationContext = createContext();

export const useJobApplications = () => {
  const context = useContext(JobApplicationContext);
  if (!context) {
    throw new Error('useJobApplications must be used within a JobApplicationProvider');
  }
  return context;
};

const initialState = {
  applications: [],
  analytics: null,
  followUps: [],
  loading: false,
  error: null,
  filters: {
    status: '',
    platform: '',
    priority: '',
    dateFrom: '',
    dateTo: '',
    sortBy: 'applied_date',
    sortOrder: 'DESC'
  }
};

const actionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_APPLICATIONS: 'SET_APPLICATIONS',
  ADD_APPLICATION: 'ADD_APPLICATION',
  UPDATE_APPLICATION: 'UPDATE_APPLICATION',
  DELETE_APPLICATION: 'DELETE_APPLICATION',
  SET_ANALYTICS: 'SET_ANALYTICS',
  SET_FOLLOW_UPS: 'SET_FOLLOW_UPS',
  SET_FILTERS: 'SET_FILTERS',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

function jobApplicationReducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case actionTypes.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    
    case actionTypes.CLEAR_ERROR:
      return { ...state, error: null };
    
    case actionTypes.SET_APPLICATIONS:
      return { ...state, applications: action.payload, loading: false };
    
    case actionTypes.ADD_APPLICATION:
      return {
        ...state,
        applications: [action.payload, ...state.applications],
        loading: false
      };
    
    case actionTypes.UPDATE_APPLICATION:
      return {
        ...state,
        applications: state.applications.map(app =>
          app.id === action.payload.id ? action.payload : app
        ),
        loading: false
      };
    
    case actionTypes.DELETE_APPLICATION:
      return {
        ...state,
        applications: state.applications.filter(app => app.id !== action.payload),
        loading: false
      };
    
    case actionTypes.SET_ANALYTICS:
      return { ...state, analytics: action.payload };
    
    case actionTypes.SET_FOLLOW_UPS:
      return { ...state, followUps: action.payload };
    
    case actionTypes.SET_FILTERS:
      return { ...state, filters: { ...state.filters, ...action.payload } };
    
    default:
      return state;
  }
}

export const JobApplicationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(jobApplicationReducer, initialState);

  const setLoading = useCallback((loading) => {
    dispatch({ type: actionTypes.SET_LOADING, payload: loading });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: actionTypes.SET_ERROR, payload: error });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: actionTypes.CLEAR_ERROR });
  }, []);

  const fetchApplications = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      clearError();
      const applications = await jobApplicationService.getAll(filters);
      dispatch({ type: actionTypes.SET_APPLICATIONS, payload: applications });
    } catch (error) {
      setError(error.message);
    }
  }, [setLoading, clearError, setError]);

  const createApplication = useCallback(async (applicationData) => {
    try {
      setLoading(true);
      clearError();
      const newApplication = await jobApplicationService.create(applicationData);
      dispatch({ type: actionTypes.ADD_APPLICATION, payload: newApplication });
      return newApplication;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, [setLoading, clearError, setError]);

  const updateApplication = useCallback(async (id, applicationData) => {
    try {
      setLoading(true);
      clearError();
      const updatedApplication = await jobApplicationService.update(id, applicationData);
      dispatch({ type: actionTypes.UPDATE_APPLICATION, payload: updatedApplication });
      return updatedApplication;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, [setLoading, clearError, setError]);

  const deleteApplication = useCallback(async (id) => {
    try {
      setLoading(true);
      clearError();
      await jobApplicationService.delete(id);
      dispatch({ type: actionTypes.DELETE_APPLICATION, payload: id });
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, [setLoading, clearError, setError]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const analytics = await jobApplicationService.getAnalytics();
      dispatch({ type: actionTypes.SET_ANALYTICS, payload: analytics });
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  }, []);

  const fetchFollowUps = useCallback(async () => {
    try {
      const followUps = await jobApplicationService.getFollowUps();
      dispatch({ type: actionTypes.SET_FOLLOW_UPS, payload: followUps });
    } catch (error) {
      console.error('Failed to fetch follow-ups:', error);
    }
  }, []);

  const setFilters = useCallback((filters) => {
    dispatch({ type: actionTypes.SET_FILTERS, payload: filters });
  }, []);

  const value = {
    ...state,
    fetchApplications,
    createApplication,
    updateApplication,
    deleteApplication,
    fetchAnalytics,
    fetchFollowUps,
    setFilters,
    clearError
  };

  return (
    <JobApplicationContext.Provider value={value}>
      {children}
    </JobApplicationContext.Provider>
  );
};